import {
    AssetApiError,
    type AssetClientContext,
    assetKeys,
    createFolder,
    deleteAsset,
    deleteFolder,
    folderKeys,
    moveAsset,
    renameAsset,
    renameFolder,
} from "@pej/asset-client";
import type { AssetSummary, CreateFolderResult, NameResult } from "@pej/kore/components";
import type { QueryClient } from "@tanstack/react-query";
import { toFolderSummary } from "./asset-projections";

/**
 * The picker's six management callbacks.
 *
 * The error contract is the substance here, not the plumbing. Read
 * `AssetPickerProps`: none of these may reject. The picker owns no error UI, so
 * a rejection becomes an unhandled rejection with nothing visible changing —
 * strictly worse than any handling the host can do.
 *
 * Three of them can fail on a name the service refuses. That is an ordinary
 * outcome, not an exception: folder names are unique per workspace, so "already
 * exists" is something an operator does routinely and the message belongs
 * inline in the field that caused it (spec §5.5). Those three resolve with a
 * result. The rest resolve on success and report failure through a toast.
 */

export interface AssetCallbacks {
    onMoveAsset: (assetId: string, folderId: string | null) => Promise<void>;
    onRenameAsset: (assetId: string, name: string) => Promise<NameResult>;
    onDeleteAsset: (asset: AssetSummary) => Promise<void>;
    onCreateFolder: (name: string) => Promise<CreateFolderResult>;
    onRenameFolder: (folderId: string, name: string) => Promise<NameResult>;
    onDeleteFolder: (folderId: string) => Promise<void>;
}

/** Reports a failure the operator should see but cannot act on inline. */
export type ReportError = (message: string) => void;

/**
 * `Conflict.AlreadyExists` is the service's name-collision code and renders as
 * HTTP 409. Keyed on the code rather than the status: a status is a category
 * and the code is the actual answer, and other 409s would otherwise be
 * mistranslated into "that name is taken".
 */
const ALREADY_EXISTS = "Conflict.AlreadyExists";

/**
 * A move or rename is accepted only on a ready asset. Note the status: kore's
 * `PreconditionFailed` renders as **422**, not 412 — a shipped client has
 * already carried that bug latent, so key on what the service actually sends.
 */
const NOT_READY_STATUS = 422;

function isNameConflict(error: unknown): boolean {
    return error instanceof AssetApiError && error.code === ALREADY_EXISTS;
}

/**
 * Turns a thrown error into the inline message a name field shows.
 *
 * A conflict gets a specific sentence; anything else gets a general one AND a
 * report, because a network outage rendering as "that name is taken" would send
 * the operator off renaming things to fix a problem that has nothing to do with
 * names.
 */
function toNameFailure(error: unknown, subject: string, report: ReportError): { ok: false; message: string } {
    if (isNameConflict(error)) {
        return { ok: false, message: `A ${subject} with that name already exists.` };
    }
    report(`Could not rename that ${subject}. Try again.`);
    return { ok: false, message: "Something went wrong. Try again." };
}

function describeFailure(error: unknown, fallback: string): string {
    if (error instanceof AssetApiError && error.status === NOT_READY_STATUS) {
        return "That image is still being checked. Try again in a moment.";
    }
    return fallback;
}

export function buildAssetCallbacks(
    ctx: AssetClientContext,
    queryClient: QueryClient,
    report: ReportError,
): AssetCallbacks {
    /** Everything an asset write can change: its page, its detail, its folder counts. */
    const invalidateAssets = () => queryClient.invalidateQueries({ queryKey: assetKeys.all(ctx.workspaceId) });
    const invalidateFolders = () => queryClient.invalidateQueries({ queryKey: folderKeys.all(ctx.workspaceId) });

    return {
        async onMoveAsset(assetId, folderId) {
            try {
                await moveAsset(ctx, assetId, folderId);
                await invalidateAssets();
            } catch (error) {
                report(describeFailure(error, "Could not move that image. Try again."));
            }
        },

        async onRenameAsset(assetId, name) {
            try {
                await renameAsset(ctx, assetId, name);
                await invalidateAssets();
                return { ok: true };
            } catch (error) {
                return toNameFailure(error, "image", report);
            }
        },

        async onDeleteAsset(asset) {
            try {
                await deleteAsset(ctx, asset.id);
                await invalidateAssets();
            } catch (error) {
                report(describeFailure(error, `Could not delete ${asset.displayName}. Try again.`));
            }
        },

        async onCreateFolder(name) {
            try {
                const folder = await createFolder(ctx, name);
                await invalidateFolders();
                // The folder itself, not just success: "create and move" and the
                // upload target select both need the id immediately, and without
                // it they would re-list and match on a name just typed.
                return { ok: true, folder: toFolderSummary(folder) };
            } catch (error) {
                return toNameFailure(error, "folder", report);
            }
        },

        async onRenameFolder(folderId, name) {
            try {
                await renameFolder(ctx, folderId, name);
                await invalidateFolders();
                // Assets carry no folder name, but the picker's rows and the
                // field's "in Brand" line read it — so both families refresh.
                await invalidateAssets();
                return { ok: true };
            } catch (error) {
                return toNameFailure(error, "folder", report);
            }
        },

        async onDeleteFolder(folderId) {
            try {
                await deleteFolder(ctx, folderId);
                // Deletion cascades to the folder's assets, so the asset cache is
                // stale too — not just the folder list.
                await invalidateFolders();
                await invalidateAssets();
            } catch {
                // No binding: unlike a move, a folder delete has no
                // error-specific message worth distinguishing.
                report("Could not delete that folder. Try again.");
            }
        },
    };
}
